/*
 * Loby gallery carousel progress line — continuous filled track, AromaTech style.
 *
 * A single thin line under the product gallery: a faint track with a dark ink
 * fill that grows left-to-right as you move through the images. Click anywhere
 * on the track to jump; arrow keys step when it's focused.
 *
 * How the gallery works on this theme:
 * The oil product template uses Dawn's `thumbnail_slider` layout. On desktop
 * Dawn normally shows one image at a time, but loby-theme.css turns the
 * `.product__media-list` into a plain horizontal overflow container (see the
 * `.product--thumbnail_slider` block there). This script owns the carousel
 * behaviour on top of that container:
 *   - mirrors scroll position onto the progress line
 *   - after a free scroll settles, eases to the nearest slide
 *   - jumps to a slide on a line click / arrow key
 * CSS scroll-snap is deliberately NOT used: on this gallery it cancels
 * programmatic scrolls and fights the progress tween. No Dawn files change.
 */
class LobyGalleryProgress {
  constructor(mediaGallery) {
    this.mediaGallery = mediaGallery;
    this.viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    this.track = this.viewer ? this.viewer.querySelector('[id^="Slider-"]') : null;
    this.slides = this.track ? Array.from(this.track.querySelectorAll('[id^="Slide-"]')) : [];

    if (!this.track || this.slides.length < 2) return;

    // The carousel images past the first are `loading="lazy"`, but in a
    // horizontal carousel they sit off-screen and only start downloading when
    // scrolled to — so you'd see a blank slide for a beat. Force them eager.
    this.slides.forEach((slide) => {
      slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
        if (img.dataset.src && !img.getAttribute('src')) img.src = img.dataset.src;
      });
    });

    this.jumpingUntil = 0;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onBarClick = this.onBarClick.bind(this);
    this.onBarKeydown = this.onBarKeydown.bind(this);
    this.settle = this.settle.bind(this);

    this.build();
    this.buildCursor();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', this.onResize);
    // The strip widens as lazy images decode after init — re-render so the fill
    // isn't stuck on a value computed against the pre-load width.
    this.resizeObserver = new ResizeObserver(() => this.render());
    this.resizeObserver.observe(this.track);
  }

  // Wrap-around step used by the arrow cursor.
  step(direction) {
    const next = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    this.animateScrollTo(this.offsetForIndex(next));
  }

  // A round follow-the-pointer cursor with a ← / → arrow, shown while hovering
  // the gallery on fine-pointer devices. The OS cursor is hidden over the strip
  // (see `.loby-has-cursor` in loby-theme.css). Clicking the image steps the
  // carousel (left half = previous, right half = next). The exception is the
  // little magnifier icon (top-left): over it the OS cursor comes back, the
  // disc hides, and a click opens Dawn's zoom instead of stepping.
  buildCursor() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.track.classList.add('loby-has-cursor');
    this.cursor = document.createElement('div');
    this.cursor.className = 'loby-gallery-cursor';
    this.cursor.setAttribute('aria-hidden', 'true');
    this.cursor.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>';
    (document.body || document.documentElement).appendChild(this.cursor);

    this.overZoom = false;
    this.zoomIcon = this.track.querySelector('.product__media-icon');

    const dirFor = (event) => {
      const rect = this.track.getBoundingClientRect();
      return event.clientX - rect.left < rect.width / 2 ? -1 : 1;
    };
    // True when the pointer is within the zoom icon's box (padded a little so
    // it's easy to hit).
    const isOverZoom = (event) => {
      if (!this.zoomIcon) return false;
      const r = this.zoomIcon.getBoundingClientRect();
      const pad = 6;
      return (
        event.clientX >= r.left - pad &&
        event.clientX <= r.right + pad &&
        event.clientY >= r.top - pad &&
        event.clientY <= r.bottom + pad
      );
    };
    // Disc position is written once per frame from the last-known pointer
    // coords, so it stays locked to the pointer even on a fast flick (no
    // moment where a stray pointer separates from it).
    let px = 0;
    let py = 0;
    let dir = 1;
    let rafId = null;
    const paintCursor = () => {
      rafId = null;
      this.cursor.style.transform =
        `translate(${px}px, ${py}px) translate(-50%, -50%)` +
        (dir < 0 ? ' rotate(180deg)' : '');
    };

    const update = (event) => {
      px = event.clientX;
      py = event.clientY;
      dir = dirFor(event);
      this.overZoom = isOverZoom(event);
      this.track.classList.toggle('loby-over-zoom', this.overZoom);
      if (this.overZoom) {
        // Over the magnifier: OS cursor comes back as a zoom affordance.
        document.documentElement.classList.remove('loby-cursor-hidden');
        this.cursor.classList.remove('is-visible');
        return;
      }
      document.documentElement.classList.add('loby-cursor-hidden');
      this.cursor.classList.add('is-visible');
      if (rafId === null) rafId = requestAnimationFrame(paintCursor);
    };
    const hide = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      document.documentElement.classList.remove('loby-cursor-hidden');
      this.cursor.classList.remove('is-visible');
      this.overZoom = false;
      this.track.classList.remove('loby-over-zoom');
    };

    // mouse* (not pointer*) — mouseenter/mouseleave fire reliably on the bound
    // element and we've already gated on a fine pointer above.
    this.track.addEventListener('mouseenter', update);
    this.track.addEventListener('mousemove', update);
    this.track.addEventListener('mouseleave', hide);
    // Safety nets: any pointer move outside the gallery, the window losing
    // focus, or the pointer leaving the document all clear the hidden-cursor
    // state so it can never get stranded page-wide.
    document.addEventListener('mousemove', (event) => {
      if (!event.target.closest || !event.target.closest('[id^="Slider-"]')) hide();
    });
    document.addEventListener('mouseleave', hide);
    window.addEventListener('blur', hide);

    // Intercept the image click before Dawn's modal-opener sees it. Over the
    // zoom icon, let it through so Dawn opens the lightbox; anywhere else, step
    // the carousel.
    this.track.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('a')) return;
        if (this.overZoom || isOverZoom(event)) return;
        event.preventDefault();
        event.stopPropagation();
        this.step(dirFor(event));
      },
      true
    );
  }

  get maxScroll() {
    return Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
  }

  // Fraction of the whole strip scrolled past — matches AromaTech: ~1/N on the
  // first image, 1 on the last, continuous in between. This is what fills the
  // line, so image 1 already reads as "1 of N" rather than an empty bar.
  get progressFraction() {
    const total = this.track.scrollWidth;
    if (total <= this.track.clientWidth) return 0;
    return Math.min((this.track.scrollLeft + this.track.clientWidth) / total, 1);
  }

  // 0..N-1 slide position, from raw scroll offset (not progressFraction).
  get currentIndex() {
    const max = this.maxScroll;
    const p = max > 0 ? this.track.scrollLeft / max : 0;
    return Math.round(p * (this.slides.length - 1));
  }

  offsetForIndex(index) {
    const clamped = Math.min(Math.max(index, 0), this.slides.length - 1);
    return (clamped / (this.slides.length - 1)) * this.maxScroll;
  }

  build() {
    this.bar = document.createElement('div');
    this.bar.className = 'loby-gallery-progress';
    this.bar.setAttribute('role', 'progressbar');
    this.bar.setAttribute('tabindex', '0');
    this.bar.setAttribute('aria-valuemin', '0');
    this.bar.setAttribute('aria-valuemax', String(this.slides.length - 1));
    this.bar.setAttribute('aria-label', 'Gallery image position');

    // Fill is painted as a gradient on the bar via the `--loby-progress`
    // custom property (see loby-theme.css) — no child element, so nothing
    // trips Dawn's `div:empty { display: none }` rule in base.css.
    this.bar.addEventListener('click', this.onBarClick);
    this.bar.addEventListener('keydown', this.onBarKeydown);

    const buttonsRow = this.viewer.querySelector('.slider-buttons');
    if (buttonsRow) {
      buttonsRow.insertAdjacentElement('afterend', this.bar);
    } else {
      this.viewer.appendChild(this.bar);
    }

    // Dawn seeds the counter total from a Liquid `media_count` that can include
    // a filtered variant image (shows "1 / 4" for a 3-image product until the
    // first scroll). Correct it to the real slide count now.
    this.totalEl = this.viewer.querySelector('.slider-counter--total');
    this.currentEl = this.viewer.querySelector('.slider-counter--current');
    if (this.totalEl) this.totalEl.textContent = String(this.slides.length);

    this.render();
  }

  onScroll() {
    // Cheap enough to run straight from the scroll event — one custom-property
    // write and one attribute set. No rAF throttle, so the fill never wedges
    // if a frame is dropped.
    this.render();

    // Ease to the nearest slide once a free (user) scroll has stopped — but not
    // while a click/key jump is in flight (its own scroll fires this handler).
    if (Date.now() < this.jumpingUntil) return;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.settle, 150);
  }

  settle() {
    if (Date.now() < this.jumpingUntil) return;
    this.jumpTo(this.currentIndex);
  }

  onResize() {
    this.render();
  }

  onBarClick(event) {
    const rect = this.bar.getBoundingClientRect();
    const pct = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    this.jumpTo(Math.round(pct * (this.slides.length - 1)));
  }

  onBarKeydown(event) {
    let target = this.currentIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target += 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target -= 1;
    else return;
    event.preventDefault();
    this.jumpTo(target);
  }

  jumpTo(index) {
    const left = this.offsetForIndex(index);
    if (Math.abs(left - this.track.scrollLeft) < 1) return;
    this.animateScrollTo(left);
  }

  // Own rAF tween rather than `scrollTo({behavior:'smooth'})`: native smooth
  // scroll is inconsistent here (it stalls mid-animation on this gallery), and
  // a hand-rolled tween gives predictable duration + easing. Suppresses the
  // settle timer for its duration so the two don't fight.
  animateScrollTo(left) {
    const start = this.track.scrollLeft;
    const distance = left - start;
    if (Math.abs(distance) < 1) return;
    if (this.tween) cancelAnimationFrame(this.tween);
    clearTimeout(this.settleTimer);

    const duration = 340;
    let startTime;
    const step = (now) => {
      if (startTime === undefined) startTime = now;
      const t = Math.min((now - startTime) / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.track.scrollLeft = start + distance * eased;
      // Keep the settle timer parked while the tween runs.
      this.jumpingUntil = Date.now() + 200;
      if (t < 1) {
        this.tween = requestAnimationFrame(step);
      } else {
        this.tween = null;
      }
    };
    this.tween = requestAnimationFrame(step);
  }

  render() {
    // Nothing to scrub through (single image, or a mobile breakpoint where the
    // list isn't an overflow container) — hide the line. Only write `hidden`
    // when it flips, so a scroll storm can't thrash layout.
    const scrollable = this.maxScroll > 0;
    if (this.bar.hidden === scrollable) this.bar.hidden = !scrollable;
    if (!scrollable) return;

    const index = this.currentIndex;
    this.bar.style.setProperty('--loby-progress', `${this.progressFraction * 100}%`);
    this.bar.setAttribute('aria-valuenow', String(index));
    if (this.currentEl) this.currentEl.textContent = String(index + 1);
    if (this.totalEl) this.totalEl.textContent = String(this.slides.length);
  }
}

function initLobyGalleryProgress() {
  document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach((mediaGallery) => {
    if (mediaGallery.lobyProgressInitialized) return;
    mediaGallery.lobyProgressInitialized = true;
    new LobyGalleryProgress(mediaGallery);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLobyGalleryProgress);
} else {
  initLobyGalleryProgress();
}

document.addEventListener('shopify:section:load', initLobyGalleryProgress);
